import React from 'react'
import { Link } from 'react-router'
import { ArrowDown, ArrowUp, Copy } from 'lucide-react'
import type { ConnectionPath } from '../../services/apiClient'
import { useCopyWithToast } from '../profile/ProfileActionLinks'

/** The path as sentences a journalist can paste: »A ejer 100 % af B. B ejer …«. */
export function connectionSentence(path: ConnectionPath): string {
  const names = new Map(path.nodes.map((n) => [n.id, n.name]))
  return path.edges
    .map(
      (e) =>
        `${names.get(e.from) ?? e.from} ${e.label} ${names.get(e.to) ?? e.to}${e.period ? ` (${e.period})` : ''}`
    )
    .join('. ') + '.'
}

function nodeHref(id: string): string | null {
  if (id.startsWith('cvr:')) return `/company/${id.slice(4)}`
  if (id.startsWith('person:')) return `/person/${id.slice(7)}`
  return null
}

interface ConnectionPathCardProps {
  path: ConnectionPath
  title: string
}

/**
 * One connection drawn in the graph's shape language: pills for persons,
 * rectangles for companies, solid links for ownership and dashed for roles.
 */
const ConnectionPathCard: React.FC<ConnectionPathCardProps> = ({ path, title }) => {
  const copyText = useCopyWithToast()
  const sentence = connectionSentence(path)
  const graphTarget = [...path.nodes].reverse().find((n) => n.id.startsWith('cvr:'))

  return (
    <article className="rounded-[10px] border border-rule bg-paper px-5 py-4" data-testid="connection-path">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[15px] font-bold text-ink">
          {title}
          <span className="ml-2 text-[13px] font-semibold text-ink-500">
            {path.edges.length === 1 ? '1 led' : `${path.edges.length} led`}
          </span>
        </h3>
      </header>

      <ol className="mt-3">
        {path.nodes.map((node, i) => {
          const edge = path.edges[i]
          const href = nodeHref(node.id)
          const isPerson = node.kind === 'person'
          const shape = isPerson ? 'rounded-full' : 'rounded-md'
          return (
            <li key={node.id}>
              {href ? (
                <Link
                  to={href}
                  className={`inline-flex max-w-full items-center border border-rule-node bg-paper px-3 py-1.5 text-sm font-semibold text-ink hover:border-ink ${shape}`}
                >
                  <span className="truncate">{node.name}</span>
                </Link>
              ) : (
                <span className={`inline-flex max-w-full items-center border border-signal bg-signal-50 px-3 py-1.5 text-sm font-semibold text-ink ${shape}`}>
                  <span className="truncate">{node.name}</span>
                </span>
              )}
              {edge && (
                <div className="flex items-stretch gap-3 py-1 pl-4">
                  <span
                    aria-hidden
                    className={`w-0 border-l-2 ${
                      edge.current === false
                        ? 'border-dotted border-ink-200'
                        : edge.kind === 'ownership'
                          ? 'border-solid border-ink-400'
                          : 'border-dashed border-ink-300'
                    }`}
                  />
                  <span className="inline-flex items-center gap-1 py-1 text-[13px] text-ink-600">
                    {edge.from === node.id ? (
                      <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                    )}
                    {edge.label}
                    {edge.period && <span className="tabular-nums text-ink-400">· {edge.period}</span>}
                  </span>
                </div>
              )}
            </li>
          )
        })}
      </ol>

      <p className="mt-3 rounded-lg bg-canvas px-3 py-2.5 text-sm leading-[1.45] text-ink-700">{sentence}</p>
      <div className="mt-2.5 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void copyText(sentence, 'Forbindelsen er kopieret.')}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-rule-strong bg-paper px-3 text-[13px] font-semibold text-ink hover:border-ink-300"
        >
          <Copy className="h-3.5 w-3.5" aria-hidden />
          Kopiér tekst
        </button>
        {graphTarget && (
          <Link
            to={`/company/${graphTarget.id.slice(4)}`}
            className="inline-flex h-8 items-center rounded-md border border-rule-strong bg-paper px-3 text-[13px] font-semibold text-ink hover:border-ink-300"
          >
            Vis {graphTarget.name} i grafen
          </Link>
        )}
      </div>
    </article>
  )
}

export default ConnectionPathCard
